'use client';

import React from 'react';
import { AngleImageDisplay } from '@/features/design/concept/ui/AngleImageDisplay';
import { RenderProgress } from './RenderProgress';
import { useSmoothRenderOverlay } from '../hooks/use-smooth-render-overlay';
import type { Job } from '@/features/design/job-queue';
import type { ImageSizingConstraints } from '@/shared/hooks';
import { ClassTypes } from '@/shared/constants';
import { cn } from '@/shared/lib/cn';
import { AIGlowBorder } from './ai-glow-border/AIGlowBorder';

export interface AngleImageWithProgressProps {
  // Image URLs
  sourceUrl?: string;
  renderUrl?: string;
  alt: string;

  // Display state
  showingRender: boolean;
  onToggle: () => void;
  isUpscaled?: boolean;

  // Active jobs for progress overlays
  activeRenderJob?: Job;
  activeUpscaleJob?: Job;

  /** A just-completed render for this angle is still prefetching its image —
   *  hold the progress overlay until it is decoded (Apple/Android parity). */
  isPreloadingRender?: boolean;

  // Angle ID for progress tracking
  conceptId?: string;
  projectId?: string;

  // Optional callbacks
  onRenderComplete?: () => void;
  onRenderError?: () => void;
  onUpscaleComplete?: () => void;
  onUpscaleError?: () => void;

  // Swipe navigation (for main angle - navigates between concepts)
  onDraggingChange?: (isDragging: boolean) => void;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;

  // Image click handler (to open fullscreen viewer)
  onImageClick?: () => void;

  // External control for viewer
  isViewerOpenExternal?: boolean;
  onViewerClose?: () => void;

  /** Per-layout sizing constraints (Apple-parity caps/floors/lift). */
  sizing: ImageSizingConstraints;
  /** External programmatic slider position — drives the compare slider
   *  to a target position when render-form mode changes (two-column mode). */
  sliderProgrammaticPosition?: number;

  /** Angle aspect ratio (width / height) known from the API, to seed the
   *  container shape and avoid a reflow when the image loads. */
  initialAspectRatio?: number;

  className?: string;
}

/**
 * Unified component for displaying angle image with render/upscale progress
 *
 * Handles:
 * - Image display with comparison slider
 * - 4K badge for upscaled renders
 * - Render progress overlay (if activeRenderJob)
 * - Upscale progress overlay (if activeUpscaleJob && !activeRenderJob)
 *
 * Use this instead of duplicating progress logic in MainView/SecondaryViews
 */
export function AngleImageWithProgress({
  sourceUrl,
  renderUrl,
  alt,
  // showingRender and onToggle are part of the interface but not used in this component
  // They're kept in the interface for consistency with AngleImageDisplay props
  showingRender: _showingRender,
  onToggle: _onToggle,
  isUpscaled = false,
  activeRenderJob,
  activeUpscaleJob,
  isPreloadingRender = false,
  conceptId,
  projectId,
  onRenderComplete,
  onRenderError,
  onUpscaleComplete,
  onUpscaleError,
  onDraggingChange,
  onSwipeLeft,
  onSwipeRight,
  onImageClick,
  isViewerOpenExternal,
  onViewerClose,
  sizing,
  sliderProgrammaticPosition,
  initialAspectRatio,
  className = '',
}: AngleImageWithProgressProps) {
  // Suppress unused variable warnings - these props are part of the interface
  void _showingRender;
  void _onToggle;

  // Smooth overlay lifecycle (Apple/Android parity): the overlay outlives the
  // job until the finished image is decoded (isPreloadingRender), then
  // dissolves over 1.2 s instead of unmounting the instant the job leaves the
  // queue. The retained job keeps the progress card rendered while it fades.
  const activeJob = activeRenderJob ?? activeUpscaleJob;
  const { job: overlayJob, phase: overlayPhase } = useSmoothRenderOverlay(
    activeJob,
    isPreloadingRender
  );
  // Un-dim the image exactly when the dissolve starts — the 600 ms
  // grayscale→color reveal of the new render plays under the fading overlay.
  const isImageDimmed = overlayPhase === 'visible' && !!overlayJob;
  // Once the live job is gone the render is done and its image is decoding —
  // pin the overlay's bar at 100% and let the parent own the dissolve.
  const isOverlayPreloading = isPreloadingRender || !activeJob;

  return (
    <div className={`relative isolate ${className}`}>
      {/* Main image display with comparison slider */}
      <AngleImageDisplay
        sourceUrl={sourceUrl}
        renderUrl={renderUrl}
        alt={alt}
        containerClassName=""
        isUpscaled={isUpscaled}
        isRendering={isImageDimmed}
        onDraggingChange={onDraggingChange}
        onSwipeLeft={onSwipeLeft}
        onSwipeRight={onSwipeRight}
        onImageClick={onImageClick}
        isViewerOpenExternal={isViewerOpenExternal}
        onViewerClose={onViewerClose}
        sizing={sizing}
        sliderProgrammaticPosition={sliderProgrammaticPosition}
        initialAspectRatio={initialAspectRatio}
      />

      {/* AI glow border (shows during processing) */}
      <AIGlowBorder isActive={activeRenderJob?.status === 'processing'} />

      {/* Render/upscale progress overlay — held through image prefetch, then
          dissolved (duration matches RENDER_OVERLAY_FADE_MS). */}
      {overlayJob && overlayPhase !== 'hidden' && (
        <div
          className={cn(
            'transition-opacity duration-[1200ms] ease-out',
            overlayPhase === 'fading' ? 'opacity-0' : 'opacity-100'
          )}
        >
          {overlayJob.jobType === 'upscale' ? (
            <RenderProgress
              queueRenderInfo={overlayJob}
              mode="inline"
              isPreloading={isOverlayPreloading}
              onComplete={onUpscaleComplete}
              onError={onUpscaleError}
            />
          ) : (
            <RenderProgress
              render={{
                type: ClassTypes.RENDER,
                id: overlayJob.id,
                attributes: {
                  status: overlayJob.status,
                  started_at: overlayJob.startedAt || '',
                  duration_predicted: overlayJob.durationPredicted,
                  priority: 0,
                  settings: {},
                  setup_hash: '',
                  created_at: overlayJob.startedAt || '',
                  updated_at: overlayJob.startedAt || '',
                },
              }}
              mode="inline"
              conceptId={conceptId}
              projectId={projectId}
              isPreloading={isOverlayPreloading}
              onComplete={onRenderComplete}
              onError={onRenderError}
            />
          )}
        </div>
      )}
    </div>
  );
}
